/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/ruleHelpers.ts (Dial Rule Restrictions)
 🗓️ Created: 2025-01-27
 🧩 Description:
    Helper functions for Dial Rule management.
────────────────────────────────────────────────────────────────*/

import { RuleFormState, DialRuleRestrictionRule } from '../types';

const PRIORITY_STEP = 10;

/**
 * Apply sequential priorities to rules
 */
export function applySequentialPriorities(rules: RuleFormState[]): RuleFormState[] {
  return rules.map((rule, index) => ({ ...rule, priority: index * PRIORITY_STEP }));
}

/**
 * Normalize rules for API payload
 */
export function normalizeRulesForApi(rules: RuleFormState[]): DialRuleRestrictionRule[] {
  return applySequentialPriorities(rules).map((rule) => ({
    id: rule.id,
    direction: rule.direction,
    pattern: rule.pattern.trim(),
    allowed: rule.allowed,
    announcement: rule.announcement?.trim() || null,
    playMaxDuration: rule.playMaxDuration,
    maxDurationSeconds: rule.playMaxDuration ? rule.maxDurationSeconds ?? null : null,
    requirePassword: rule.requirePassword,
    priority: rule.priority,
    enabled: rule.enabled,
  }));
}

/**
 * Serialize form state for API
 */
export function serializeFormState(state: {
  name: string;
  description: string;
  enabled: boolean;
  rules: RuleFormState[];
}): {
  name: string;
  description: string;
  enabled: boolean;
  rules: DialRuleRestrictionRule[];
} {
  return {
    name: state.name.trim(),
    description: state.description.trim(),
    enabled: state.enabled,
    rules: normalizeRulesForApi(state.rules),
  };
}

